import { yupResolver } from '@hookform/resolvers/yup'
import { Box, FormControl, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { FeatureFlagIdAiTicketing, FeatureFlagIdNewSearchEntitlementsEndpoint, FeatureFlagIdRequestSchema } from 'c1gen/features'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1CopyDataAsURLButton } from 'components/core/button/C1CopyDataAsURLButton'
import { c1FormToYupSchema, RecordStringAny } from 'components/core/dynamic-form/c1FormToYupSchema'
import { RenderField } from 'components/core/dynamic-form/fields/RenderField'
import { APIError } from 'components/core/errors/api'
import { TaskAPIError } from 'components/core/errors/task-api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { SafeMarkdown } from 'components/core/markdown'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledTextField } from 'components/core/RHKFormComponents/C1LabeledTextField'
import { periods } from 'components/core/time/duration'
import { useFeatureChecker } from 'components/no-ui/capability'
import { AccessRequestFormDurationField } from 'components/pages/accessrequest/create/form-v2/fields/AccessRequestFormDurationField'
import { AccessRequestFormEntFieldV2 } from 'components/pages/accessrequest/create/form-v2/fields/AccessRequestFormEntFieldV2'
import { formToProtoStruct, getDefaultValues } from 'components/pages/entitlement/request-schema/fieldConversion'
import { TaskDrawer } from 'components/pages/task/details/drawer/TaskDrawer'
import { useAlert } from 'hooks/useAlert/index'
import { useDrawer } from 'hooks/useDrawer'
import { AppValue } from 'lib/actions/apps'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user'
import { UserContext } from 'lib/context/app'
import { unsafeRoute } from 'lib/routes'
import { Info } from 'lucide-react'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { Form } from 'pbts/c1/api/form/v1/form'
import { JustificationVisibility, RequestSchemaServiceFindForAppEntitlementsRequest, RequestSchemaWithBinding } from 'pbts/c1/api/request_schema/v1/request_schema'
import { TaskGrantSource, TaskServiceCreateGrantRequest, TaskServiceCreateManyGrantTasksRequest } from 'pbts/c1/api/task/v1/task'
import { Duration } from 'pbts/google/protobuf/duration'
import { Struct } from 'pbts/google/protobuf/struct'
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, ControllerRenderProps, FormProvider, Resolver, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { searchAppUsersApi } from 'redux_toolkit/app-users'
import { findForAppEntitlementsApi } from 'redux_toolkit/request-schema'
import { createGrantTaskApi, createManyGrantTasksApi } from 'redux_toolkit/tasks'
import * as yup from 'yup'
import { RenderAppUserForDialog, RenderEntitlementsForDialog, RenderUserForDialog } from './DialogComponents'
import { AccessRequestFormAppField } from './fields/AccessRequestFormAppField'
import { AccessRequestAppUserField } from './fields/AccessRequestFormAppUserField'
import { AccessRequestFormEmergencyField } from './fields/AccessRequestFormEmergencyField'
import { AccessRequestFormEntField } from './fields/AccessRequestFormEntField'
import { AccessRequestFormUserField } from './fields/AccessRequestFormUserField'

export type AccessRequest = {
    user: UserValue,
    app: AppValue,
    appUser: AppUserValue,
    ents: Array<AppEntitlementValue>,
    provisionDurationSeconds: number,
    justification: string,
    access: string,
    external_url?: string,
    emergencyAccess: boolean,
    requestData?: RecordStringAny,
}

type RequestAccessFormV2Props = {
    grantSource?: TaskGrantSource
    defaultValues?: AccessRequest
    variant?: 'default' | 'dialog'
    onSubmitCallback?: () => void
    onCancel?: () => void
    showCopyLink?: boolean
    conversationId?: string
}

type validatorType = (intl: IntlShape, requestSchemaWithBindingToUse?: RequestSchemaWithBinding) => Resolver<AccessRequest, unknown>

const getSchema = (intl: IntlShape, requestSchemaWithBindingToUse?: RequestSchemaWithBinding): yup.ObjectSchema<AccessRequest> => {
    const hideJustification = requestSchemaWithBindingToUse?.requestSchema?.justificationVisibility === JustificationVisibility.HIDE

    return yup.object().shape({
        user: yup
            .mixed()
            .required(
                intl.formatMessage({ id: 'field_error_user_required', defaultMessage: 'You must select a user' }),
            ),
        app: yup
            .mixed()
            .required(intl.formatMessage({ id: 'field_error_app_required', defaultMessage: 'You must select an app' })),
        appUser: yup
            .mixed()
            .nullable(),
        ents: yup.array().of(yup.mixed().required())
            .max(10, intl.formatMessage({
                id: 'field_error_too_many_entitlements',
                defaultMessage: 'You may select up to 10 entitlements at a time',
            }))
            .min(1, intl.formatMessage({
                id: 'field_error_at_least_one_entitlement',
                defaultMessage: 'You must select at least one entitlement',
            }))
            .required(),
        provisionDurationSeconds: yup
            .number()
            .required(
                intl.formatMessage({ id: 'field_error_duration_required', defaultMessage: 'You must pick a duration' }),
            ),
        justification: hideJustification
            ? yup.string().optional()
            : yup.string().required(
                intl.formatMessage({
                    id: 'field_error_justification_required',
                    defaultMessage: 'You must include a justification',
                }),
            ),
        access: yup.string().required(
            intl.formatMessage({
                id: 'field_error_access_required',
                defaultMessage: 'You must select an access type',
            }),
        ),
        external_url: yup.string().url(
            intl.formatMessage({
                id: 'field_error_external_url_invalid',
                defaultMessage: 'You must enter a valid URL',
            }),
        ).optional(),
        emergencyAccess: yup.boolean().required().nonNullable(),
        requestData: requestSchemaWithBindingToUse?.requestSchema
            ? c1FormToYupSchema(Form.create({ fields: requestSchemaWithBindingToUse.requestSchema?.form?.fields }), intl)
            : yup.object().optional(),
    }) as yup.ObjectSchema<AccessRequest>
}

const getResolver: validatorType = ((intl: IntlShape, requestSchemaWithBindingToUse?: RequestSchemaWithBinding) => {
    const schema = getSchema(intl, requestSchemaWithBindingToUse)

    return yupResolver(schema)
})

export const RequestAccessFormV2: FC<RequestAccessFormV2Props> = ({
    grantSource,
    defaultValues,
    variant = 'default',
    onSubmitCallback,
    onCancel,
    showCopyLink = true,
    conversationId,
}) => {
    const intl = useIntl()
    const userContext = useContext(UserContext)
    userContext.waitForReady()
    const hasAiTicketing = useFeatureChecker(FeatureFlagIdAiTicketing)
    const [requestSchemaWithBinding, setRequestSchemaWithBinding] = useState<RequestSchemaWithBinding | undefined>(undefined)
    const resolver = useMemo(() => getResolver(intl, requestSchemaWithBinding), [intl, requestSchemaWithBinding])
    const [showAppUsers, setShowAppUsers] = useState(false)
    const [formError, setFormError] = useState<RpcError | null>(null)
    const hasRequestSchemaFeature = useFeatureChecker(FeatureFlagIdRequestSchema)

    const hasNewSearchEntitlementsEndpoint = useFeatureChecker(FeatureFlagIdNewSearchEntitlementsEndpoint)

    const toast = useAlert()
    const [showDrawer, closeDrawer] = useDrawer()

    // ==== APIs ====
    const {
        mutate: createManyGrantTasks,
    } = createManyGrantTasksApi()

    const {
        mutate: createGrantTask,
    } = createGrantTaskApi()

    // ==== Form ====
    const methods = useForm({
        resolver,
        mode: 'onTouched',
        defaultValues: {
            ...defaultValues,
            access: 'general',
            external_url: grantSource?.externalUrl,
            requestData: undefined,
        },
    }) as any // eslint-disable-line @typescript-eslint/no-explicit-any

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting, isDirty },
        setValue,
        watch,
        register,
        reset,
        resetField,
    } = methods

    // ==== Form Handlers ====
    const onSubmit = useCallback(async (fields: AccessRequest): Promise<void> => {
        if (!fields.ents) return
        const singleReq = TaskServiceCreateGrantRequest.create({
            description: fields.justification,
            identityUserId: fields.appUser ? undefined : fields.user.id,
            appId: fields.app?.id,
            appEntitlementId: fields.ents[0]?.id,
            grantDuration:
                fields.provisionDurationSeconds === periods.INDEF
                    ? undefined
                    : Duration.create({ seconds: fields.provisionDurationSeconds }),
            emergencyAccess: fields.emergencyAccess,
            source: undefined,
            appUserId: fields.appUser?.id ?? undefined,
            requestData: (requestSchemaWithBinding && fields.requestData) ?
                formToProtoStruct(fields.requestData, requestSchemaWithBinding.requestSchema?.form?.fields) : undefined,
            expandMask: { paths: ['*'] },
        })

        const externalUrl = fields.external_url
        if (grantSource?.integrationId !== undefined && hasAiTicketing && externalUrl) {
            singleReq.source = {
                integrationId: grantSource.integrationId, // ServiceDeskIntegration
                externalUrl,
                requestId: grantSource.requestId,
                conversationId: conversationId || '',
                isExtension: grantSource?.isExtension ?? false,
            }
        } else if (conversationId) {
            // Set source with conversationId even if no external integration
            singleReq.source = {
                integrationId: '',
                externalUrl: '',
                requestId: '',
                conversationId,
                isExtension: grantSource?.isExtension ?? false,
            }
        }

        const multiReq = TaskServiceCreateManyGrantTasksRequest.create({
            ...singleReq,
            appEntitlementRefs: fields.ents?.map((e) => AppEntitlementRef.create({
                id: e.id,
                appId: e.appId,
            })),
        })

        try {
            if (fields.ents.length === 1) {
                const { data: task } = await createGrantTask(singleReq)

                if (!task) return

                showDrawer(<TaskDrawer taskId={task.id} close={closeDrawer} />, { variant: 'md' })
            } else {
                const { data: tasks } = await createManyGrantTasks(multiReq)

                if (!tasks) return

                if (tasks.length === 1) {
                    showDrawer(<TaskDrawer taskId={tasks[0].id} close={closeDrawer} />, { variant: 'md' })
                } else if (fields.user.id === userContext.user.id) {
                    toast(<FormattedMessage
                        id="task_create_success_multiple"
                        defaultMessage="Your access requests have been created. Click <a>here</a> to view your open requests."
                        values={{
                            a: (chunks) => <C1Link href={unsafeRoute(`/requests/my-requests?userId=${userContext.user.id}`)}>{chunks}</C1Link>,
                        }}
                    />, 'success', 0)
                } else {
                    toast(<FormattedMessage
                        id="task_create_success_multiple"
                        defaultMessage="Your access requests for <em>{user}</em> have been created. Click <a>here</a> to view their open requests."
                        values={{
                            a: (chunks) => <C1Link href={unsafeRoute(`/requests/my-requests?userId=${fields.user.id}`)}>{chunks}</C1Link>,
                            em: (chunks) => <em>{chunks}</em>,
                            user: fields.user.displayName,
                        }}
                    />, 'success', 0)
                }
            }
            onSubmitCallback?.()
            setFormError(null)
            setValue('ents', [])
            setValue('provisionDurationSeconds', periods.INDEF)
            setValue('emergencyAccess', false)
        } catch (err) {
            setFormError(err as unknown as RpcError)
        }
    }, [grantSource, hasAiTicketing, setValue, createGrantTask, showDrawer, closeDrawer,
        createManyGrantTasks, userContext.user.id, toast, onSubmitCallback, requestSchemaWithBinding, conversationId])

    const prevValues = useRef<AccessRequest>({} as unknown as AccessRequest)

    // Watch all form values
    const currentValues = watch()
    const newValues = currentValues
    const oldValues = prevValues.current
    prevValues.current = newValues

    const { app, user } = currentValues
    const canFetchAppUsers = !!app?.id && !!user?.id

    const {
        data: appUserData,
        isLoading: isLoadingAppUser,
        isError: isErrorAppUser,
        error: appUserError,
    } = searchAppUsersApi.useSearch(AppUserServiceSearchRequest.create({
        appId: app?.id,
        userIds: [user?.id],
        expandMask: { paths: ['*'] },
    }), { actionable: canFetchAppUsers })

    const userChanged = !newValues.user || newValues.user?.id !== oldValues.user?.id
    const appChanged = !newValues.app || newValues.app?.id !== oldValues.app?.id
    const entsChanged = newValues.ents?.length === 0 || newValues.ents?.length !== oldValues.ents?.length

    const { data: entRequestSchemas } = findForAppEntitlementsApi
        .useFindForAppEntitlements(RequestSchemaServiceFindForAppEntitlementsRequest.create({
            entitlementRefs: currentValues.ents.map((ent) => AppEntitlementRef.create({
                id: ent.id,
                appId: ent.appId,
            })),
        }), { actionable: !!hasRequestSchemaFeature && currentValues.ents?.length > 0 })

    useEffect(() => {
        const schemasWithBinding = entRequestSchemas?.requestSchemasEntitlementWithBindings

        if (!schemasWithBinding?.length) {
            setRequestSchemaWithBinding(undefined)
            setValue('requestData', {}, { shouldDirty: false, shouldValidate: true })
            return
        }

        // Check if all schemas are the same
        const firstSchemaId = schemasWithBinding[0]?.requestSchema?.id
        const allSchemasSame = schemasWithBinding.every((schema) =>
            schema.requestSchema?.id === firstSchemaId)

        if (!allSchemasSame || !firstSchemaId) {
            setRequestSchemaWithBinding(undefined)
            setValue('requestData', {}, { shouldDirty: false, shouldValidate: true })
            return
        }

        // if theyre all the same we can use the schema
        const selectedSchema = schemasWithBinding[0]
        setRequestSchemaWithBinding(selectedSchema)

        const initialFields = getDefaultValues(
            Struct.create({ fields: {} }),
            selectedSchema.requestSchema?.form?.fields,
        )

        // Only initialize if user has nbont inputted data
        const currentData = methods.getValues('requestData') as RecordStringAny | undefined
        const isEmpty = !currentData || Object.keys(currentData).length === 0

        if (isEmpty) {
            setValue('requestData', initialFields as unknown as RecordStringAny, {
                shouldDirty: false,
                shouldValidate: true,
            })
        }
    }, [entRequestSchemas, setValue, methods])

    useEffect(() => {
        if (!isDirty) return

        if (userChanged) {
            setShowAppUsers(false)
            resetField('app')
            resetField('ents')
            resetField('provisionDurationSeconds')
            resetField('emergencyAccess')
            resetField('requestData')
            setRequestSchemaWithBinding(undefined)
        }

        if (appChanged) {
            setShowAppUsers(false)
            resetField('ents')
            resetField('provisionDurationSeconds')
            resetField('emergencyAccess')
            resetField('requestData')
            setRequestSchemaWithBinding(undefined)
        }

        if (entsChanged) {
            resetField('provisionDurationSeconds')
            resetField('emergencyAccess')
            resetField('requestData')
        }
    }, [userChanged, appChanged, entsChanged, resetField, setShowAppUsers, isDirty])

    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues)
        }
    }, [defaultValues, reset])

    const appUsers = appUserData[0]
    useEffect(() => {
        // If we give control to the user, they keep it
        if (showAppUsers) return

        if (!canFetchAppUsers || isLoadingAppUser || isErrorAppUser) return

        if (appUsers === undefined) return

        // Attempt to load app users
        if (appUsers.length === 0) setValue('appUser', null as unknown as AppUserValue)
        if (appUsers.length === 1) setValue('appUser', appUsers[0] as unknown as AppUserValue)

        // Transfer control to the user to select an app user
        if (appUsers.length > 1) {
            setShowAppUsers(true)
            if (!defaultValues?.appUser) setValue('appUser', null as unknown as AppUserValue)
        }
    }, [canFetchAppUsers, appUsers, isErrorAppUser, isLoadingAppUser, setValue, showAppUsers, defaultValues?.appUser])

    const formDisabled = !isValid || isSubmitting || isLoadingAppUser || isErrorAppUser

    const urlData = useMemo(() => ({
        appId: currentValues.app?.id,
        grantDuration: currentValues.provisionDurationSeconds,
        reason: currentValues.justification,
        isEmergencyAccess: currentValues.emergencyAccess,
        entitlementIds: currentValues.ents?.map((ent) => ent.id),
    }), [currentValues])

    const renderUserForDialog = variant === 'dialog' && !defaultValues?.appUser && defaultValues?.user
    const renderAppUserForDialog = variant === 'dialog' && !!defaultValues?.appUser

    return <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
                {formError && <TaskAPIError
                    err={formError as unknown as RpcError}
                    title={<FormattedMessage
                        id="component_access_request_create_error"
                        defaultMessage="Error: Failed to create access request"
                    />}
                />}

                {renderUserForDialog && <RenderUserForDialog user={defaultValues?.user} />}
                {variant === 'default' && <FormControl error={!!errors.user} fullWidth>
                    <AccessRequestFormUserField control={control} />
                </FormControl>}

                {variant === 'default' && <FormControl error={!!errors.app} fullWidth>
                    <C1FormLabel
                        label={intl.formatMessage({
                            id: 'component_access_request_app_input_label',
                            defaultMessage: 'Which app?',
                        })}
                        htmlFor="app"
                    />
                    <AccessRequestFormAppField control={control} autoFocus />
                </FormControl>}

                {app?.instructions && (
                    <Box
                        mb={2}
                    >
                        <Box mb={1}>
                            <Typography variant="h6" component="label">
                                <FormattedMessage
                                    id="component_access_request_app_instructions"
                                    defaultMessage="Instructions"
                                />
                            </Typography>
                        </Box>
                        <Box sx={{ overflowWrap: 'anywhere' }}>
                            <SafeMarkdown>
                                {app.instructions}
                            </SafeMarkdown>
                        </Box>
                    </Box>
                )}

                {renderAppUserForDialog ? <RenderAppUserForDialog appUser={defaultValues.appUser} />
                    : showAppUsers && app?.id && user?.id && <Grid item xs={12} zeroMinWidth>
                        <FormControl error={!!errors.appUser} fullWidth>
                            <C1FormLabel
                                label={intl.formatMessage({
                                    id: 'component_access_request_app_user_input_label',
                                    defaultMessage: 'Which account needs access?',
                                })}
                                htmlFor="appUser"
                            />
                            <AccessRequestAppUserField
                                control={control}
                                error={errors.appUser?.message}
                                disabled={!showAppUsers}
                                appId={app?.id}
                                userId={user?.id}
                            />
                        </FormControl>
                    </Grid>}

                {isErrorAppUser && <APIError
                    err={appUserError as unknown as RpcError}
                    title={<FormattedMessage
                        id="app_user_fetch_error"
                        defaultMessage="Error: Failed to fetch app users. Please reload the page and try again."
                    />}
                />}

                {variant === 'default' ? hasNewSearchEntitlementsEndpoint
                    ? <AccessRequestFormEntFieldV2 control={control} errors={errors} />
                    : <AccessRequestFormEntField control={control} errors={errors} /> :
                    <RenderEntitlementsForDialog entitlements={currentValues.ents} />
                }

                {requestSchemaWithBinding?.requestSchema &&
                    <Stack gap={2}>
                        {requestSchemaWithBinding.requestSchema?.form?.fields?.map((field) => <Box key={field.name}>
                            <Controller
                                name={`requestData.${field.name}` as keyof AccessRequest}
                                control={control}
                                render={({ field: cField }): React.ReactElement => <RenderField
                                    field={field}
                                    cField={cField as unknown as ControllerRenderProps}
                                    errors={errors}
                                    disabled={false}
                                />}
                            />
                            {errors.requestData?.[field.name] && <Box ml={2} mt={1}>
                                <Typography color="error">{errors.requestData?.[field.name]?.message as string}</Typography>
                            </Box>}
                        </Box>)}
                    </Stack>}

                <AccessRequestFormDurationField control={control} errors={errors} />

                {requestSchemaWithBinding?.requestSchema?.justificationVisibility !== JustificationVisibility.HIDE && <C1LabeledTextField
                    fullWidth
                    register={register}
                    label={
                        <FormattedMessage
                            id="component_access_request_justification_label"
                            defaultMessage="Why is this access needed?"
                        />
                    }
                    placeholder={intl.formatMessage({
                        id: 'component_access_request_justification_placeholder',
                        defaultMessage: 'Tell the reviewer why you are making this request.',
                    })}
                    multiline
                    rows={4}
                    name="justification"
                    error={!!errors.justification}
                    helperText={errors.justification?.message}
                    data-testid="access-request-justification-input"
                />}

                {hasAiTicketing && grantSource !== undefined && <C1LabeledTextField
                    register={register}
                    label={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" component="span">
                                <FormattedMessage
                                    id="component_access_request_external_url_label"
                                    defaultMessage="External reference link"
                                />
                            </Typography>
                            <Tooltip
                                title={
                                    <FormattedMessage
                                        id="component_access_request_external_url_tooltip"
                                        defaultMessage="If this request is associated with an external ticket, please provide the URL here."
                                    />
                                }
                                placement="top-start"
                            >
                                <C1Icon icon={Info} sx={{ color: 'info.main' }} />
                            </Tooltip>
                        </Stack>
                    }
                    placeholder={intl.formatMessage({
                        id: 'component_access_request_external_url_placeholder',
                        defaultMessage: 'e.g https://yourcompany.atlassian.net/browse/TICKET-XXXX',
                    })}
                    name="external_url"
                    error={!!errors.external_url}
                    helperText={errors.external_url?.message}
                    inputProps={{
                        readOnly: true,
                    }}
                />
                }

                <AccessRequestFormEmergencyField control={control} error={errors.emergencyAccess?.message} />

                <Box sx={{ display: 'flex', justifyContent: showCopyLink ? 'space-between' : 'flex-end', flex: 1 }}>
                    {showCopyLink && <C1CopyDataAsURLButton
                        data={urlData}
                        basePath="/access-requests/create/grant-request"
                        fieldsToValidateMaxLength={['reason']}
                    />}

                    <Stack direction="row" spacing={2}>
                        {onCancel && <C1Button variant="outlined" color="info" onClick={onCancel}>
                            <FormattedMessage id="component_access_request_cancel_button_label" defaultMessage="Cancel" />
                        </C1Button>}
                        <C1LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={formDisabled}
                            loading={isSubmitting}
                            data-testid="access-request-submit-button"
                        >
                            <FormattedMessage
                                id="component_access_request_submit_request_button_label"
                                defaultMessage="Submit request"
                            />
                        </C1LoadingButton>
                    </Stack>
                </Box>
            </Stack>
        </form>
    </FormProvider>
}
